if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector(".product-carousel-dots"),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        // Initialize dot navigation
        if (this.elements.dots) {
          this.initializeDots();
        }

        // Register slideChanged listener for both dots and thumbnails
        // This must happen BEFORE checking thumbnails existence
        this.elements.viewer.addEventListener(
          "slideChanged",
          this.onSlideChanged.bind(this),
        );

        // Early return only for thumbnail-specific setup
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      initializeDots(retryCount = 0) {
        const dots = this.elements.dots.querySelectorAll(".carousel-dot");
        const slider = this.elements.viewer.querySelector(".slider");
        if (!slider) return;

        if (slider.clientWidth === 0 && retryCount < 5) {
          // Layout not computed yet, defer to next frame (max 5 retries)
          requestAnimationFrame(() => this.initializeDots(retryCount + 1));
          return;
        }

        dots.forEach((dot) => {
          dot.addEventListener("click", (e) => {
            e.preventDefault();

            // Find slide by data-media-id (not by index, since order may differ)
            const mediaId = dot.dataset.mediaId;
            const targetSlide = slider.querySelector(
              `[data-media-id="${mediaId}"]`,
            );
            if (targetSlide) {
              slider.scrollTo({
                left: targetSlide.offsetLeft,
                behavior: "smooth",
              });
            }
          });
        });
      }

      updateDots(currentMediaId) {
        if (!this.elements.dots) return;

        // Match dots by data-media-id (not by index, since order may differ from slides)
        const dots = this.elements.dots.querySelectorAll(".carousel-dot");
        const shortId = currentMediaId.split("-").pop();

        dots.forEach((dot) => {
          const dotMediaId = dot.dataset.mediaId;
          // Match full ID or short ID suffix
          const isActive =
            dotMediaId === currentMediaId ||
            dotMediaId === shortId ||
            dotMediaId?.endsWith(`-${shortId}`) ||
            currentMediaId?.endsWith(`-${dotMediaId?.split("-").pop()}`);
          dot.setAttribute("aria-current", isActive ? "true" : "false");
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        // Always update dots first (works with or without thumbnails)
        this.updateDots(mediaId);

        // Only update thumbnails if they exist
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`,
          );
          this.setActiveThumbnail(thumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          // Reorder dots to match slider order
          if (this.elements.dots) {
            const activeDot = this.elements.dots.querySelector(
              `[data-media-id="${mediaId}"]`,
            );
            if (activeDot && activeDot.parentElement.firstChild !== activeDot) {
              activeDot.parentElement.prepend(activeDot);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        // Update dots immediately when media changes
        this.updateDots(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
